import { ArrowRight } from '@/app/icons/ArrowRight'
import classNames from 'classnames'
import { useEffect, useRef, useState } from 'react'
import { Panel, PanelProps } from '../Panel/Panel'

type CollapsiblePanelProps = React.PropsWithChildren<
  {
    header: React.ReactNode
    defaultOpen?: boolean
    noToggle?: boolean
    summaryProps?: React.DetailsHTMLAttributes<HTMLDivElement>
  } & PanelProps
>

export function CollapsiblePanel({
  children,
  header,
  defaultOpen = false,
  noToggle = false,
  summaryProps,
  ...props
}: CollapsiblePanelProps) {
  const detailsElRef = useRef<HTMLDetailsElement | null>(null)
  const [isOpen, setOpen] = useState(defaultOpen)

  useEffect(() => {
    if (!detailsElRef.current) return

    const detailRef = detailsElRef.current

    const handleToggle = ({ currentTarget }) => {
      setOpen(currentTarget.open ?? false)
    }

    detailRef.addEventListener?.('toggle', handleToggle)

    return () => {
      detailRef.removeEventListener?.('toggle', handleToggle)
    }
  }, [detailsElRef, setOpen])

  return (
    <Panel
      {...props}
      className={classNames(
        '!md:p6 group flex cursor-pointer flex-row !p-4',
        'text-mydra-black transition-shadow focus-within:outline',
        'hover:bg-mydra-gray hover:shadow-course-content-item',
        { 'h-full': isOpen },
        props.className
      )}
      onClick={(e) => {
        if (e.currentTarget !== e.target) {
          return
        }
        setOpen((v) => !v)
      }}
    >
      <details className="w-full" ref={detailsElRef} open={isOpen}>
        <summary
          {...summaryProps}
          className={`list-none font-semibold focus:outline-none ${summaryProps} [&::-webkit-details-marker]:hidden`}
        >
          {header}
        </summary>
        <div
          className={`text-gray-light ${isOpen ? 'cursor-pointer' : ''}`}
          onClick={(e) => {
            e.stopPropagation()
            if (isOpen) {
              setOpen(false)
            }
          }}
        >
          {children}
        </div>
      </details>
      {!noToggle && (
        <button
          className="ml-auto shrink-0 appearance-none p-2 transition-transform ease-in focus:outline-none group-hover:rotate-180"
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            setOpen((isOpen) => !isOpen)
          }}
        >
          <ArrowRight
            className={`w-2 text-[#000] md:w-3 ${isOpen ? 'rotate-90' : '-rotate-90'}`}
          />
        </button>
      )}
    </Panel>
  )
}
